// frontend/src/components/production-sequence/LineConfigEditor.tsx
// Selector de línea (combo buscable) + bloque de configuración de la línea elegida
// (turnos + UD/turno + capacidad). Edita lineConfigs[lineId] vía onChange. El combo
// es independiente de la pestaña activa: solo decide QUÉ línea se configura.
import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { calcularCapacidadDiaria } from '@/utils/sequenceCalculations';
import { ALL_LINES } from '@/hooks/useProductionSequence';
import type { LineId, LineConfig } from '@/types/productionSequence';

const FALLBACK: LineConfig = { lineId: ALL_LINES, turnos: 1, udTurnoPromedio: 2000, upm: 150 };

interface Props {
  lines: LineId[];
  lineConfigs: Record<LineId, LineConfig>;
  selectedLine: LineId;
  dark: boolean;
  onChange: (lineId: LineId, partial: Partial<LineConfig>) => void;
}

export function LineConfigEditor({ lines, lineConfigs, selectedLine, dark, onChange }: Props) {
  const { t } = useTranslation();
  const real = useMemo(() => lines.filter(l => l !== ALL_LINES), [lines]);

  const [editLineId, setEditLineId] = useState<LineId>(() =>
    real.includes(selectedLine) ? selectedLine : (real[0] ?? selectedLine),
  );
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('mousedown', onDoc); window.removeEventListener('keydown', onKey); };
  }, [open]);

  const filtered = real.filter(l => l.toLowerCase().includes(query.trim().toLowerCase()));
  const cfg = lineConfigs[editLineId] ?? { ...FALLBACK, lineId: editLineId };
  const cap = calcularCapacidadDiaria(cfg);

  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const trigger = `w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-[13px] cursor-pointer ${
    dark ? 'bg-slate-800 border-white/10 text-slate-100 hover:bg-slate-700/60' : 'bg-white border-slate-200 text-slate-800 hover:bg-slate-50'
  }`;
  const dropdown = `absolute z-20 mt-1 w-full rounded-lg border shadow-xl overflow-hidden ${
    dark ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'
  }`;
  const searchInput = `w-full px-2.5 py-1.5 text-[12px] border-b outline-none ${
    dark ? 'bg-slate-900 border-white/10 text-slate-100 placeholder:text-slate-500' : 'bg-white border-slate-200 text-slate-800 placeholder:text-slate-400'
  }`;
  const option = (on: boolean) => `w-full text-left px-3 py-1.5 text-[12px] cursor-pointer flex items-center gap-1.5 ${
    on ? (dark ? 'bg-sky-500/15 text-sky-300' : 'bg-sky-50 text-sky-700')
       : (dark ? 'text-slate-300 hover:bg-white/5' : 'text-slate-700 hover:bg-slate-50')
  }`;
  const turnoBtn = (on: boolean) => `px-2.5 py-1 rounded text-[11px] font-bold cursor-pointer ${
    on ? 'bg-sky-500 text-white' : dark ? 'bg-white/8 text-slate-300' : 'bg-slate-100 text-slate-600'
  }`;
  const inputCls = `w-28 px-2 py-1 rounded border text-[12px] tabular-nums ${
    dark ? 'bg-slate-900 border-white/10 text-slate-100' : 'bg-white border-slate-200 text-slate-800'
  }`;
  const activeTag = <span className="text-[9px] font-bold uppercase px-1 py-0.5 rounded bg-sky-500/20 text-sky-500">{t('sequence.priority.lineActive')}</span>;

  return (
    <div className="flex flex-col gap-3">
      {/* Combo buscable */}
      <div ref={boxRef} className="relative">
        <span className={`block text-[11px] mb-1 ${muted}`}>{t('sequence.priority.lineSelectLabel')}</span>
        <button type="button" onClick={() => setOpen(o => !o)} className={trigger}>
          <span className="inline-flex items-center gap-1.5 truncate">
            <span className="font-semibold truncate">{editLineId}</span>
            {editLineId === selectedLine && activeTag}
          </span>
          <svg className={`w-3 h-3 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 3.5l3 3 3-3" /></svg>
        </button>
        {open && (
          <div className={dropdown}>
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={t('sequence.priority.lineSearchPlaceholder')}
              className={searchInput}
            />
            <div className="max-h-56 overflow-y-auto">
              {filtered.map(l => (
                <button key={l} type="button" onClick={() => { setEditLineId(l); setOpen(false); setQuery(''); }} className={option(l === editLineId)}>
                  <span className="truncate">{l}</span>
                  {l === selectedLine && activeTag}
                </button>
              ))}
              {filtered.length === 0 && <div className={`px-3 py-2 text-[12px] ${muted}`}>—</div>}
            </div>
          </div>
        )}
      </div>

      {/* Bloque de config de la línea elegida */}
      <div className={`rounded-lg border px-3 py-2.5 ${dark ? 'border-white/10 bg-slate-800/40' : 'border-slate-200 bg-white'}`}>
        <div className="flex items-center gap-2 flex-wrap mb-1.5">
          <span className={`text-[11px] w-16 shrink-0 ${muted}`}>{t('sequence.priority.turnos')}</span>
          <div className="flex items-center gap-1">
            {([1, 2, 3] as const).map(tn => (
              <button key={tn} type="button" onClick={() => onChange(editLineId, { turnos: tn })} className={turnoBtn(cfg.turnos === tn)}>
                {tn}T
              </button>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 text-[12px]">
          <span className="w-16 shrink-0">{t('sequence.priority.udTurno')}</span>
          <input
            type="number"
            value={cfg.udTurnoPromedio}
            onChange={e => onChange(editLineId, { udTurnoPromedio: Number(e.target.value) })}
            className={inputCls}
          />
        </label>
        <div className={`mt-2 text-[11px] ${muted}`}>
          {t('sequence.priority.capacityCalc')}:{' '}
          <span className={`font-bold tabular-nums ${dark ? 'text-slate-200' : 'text-slate-700'}`}>{cap.toLocaleString('es-ES')}</span>{' '}
          {t('sequence.priority.capacityUnit')}
        </div>
      </div>
    </div>
  );
}
